import React, { useState, useMemo } from 'react';
import { ChevronLeft, Eye, AlertTriangle, CheckCircle, Database, FileText, Download } from 'lucide-react';

const ImportPreview = ({
  data,
  fieldMappings,
  columnTypes,
  target,
  targetConfig,
  onConfirm,
  onBack,
  isProcessing
}) => {
  const [showAllRows, setShowAllRows] = useState(false);
  const [selectedDuplicateAction, setSelectedDuplicateAction] = useState('skip');

  const processedData = useMemo(() => {
    const mappedColumns = Object.keys(fieldMappings);
    const reverseMappings = Object.fromEntries(
      Object.entries(fieldMappings).map(([col, field]) => [field, col])
    );

    return data.rows.map((row, index) => {
      const processedRow = { _rowIndex: index + 2 }; // +2 because headers are row 1

      // Map each field using the mappings
      Object.entries(fieldMappings).forEach(([sourceColumn, targetField]) => {
        const columnIndex = data.headers.indexOf(sourceColumn);
        if (columnIndex !== -1) {
          let value = row[columnIndex];

          // Apply data type transformations
          const dataType = columnTypes[sourceColumn];
          if (value !== null && value !== undefined && value !== '') {
            switch (dataType) {
              case 'number':
                value = isNaN(value) ? value : parseFloat(value);
                break;
              case 'boolean':
                value = /^(true|yes|y|1)$/i.test(value.toString());
                break;
              case 'json':
                try {
                  value = JSON.parse(value);
                } catch (e) {
                  // Keep as string if parsing fails
                }
                break;
              case 'date':
                const parsed = Date.parse(value);
                if (!isNaN(parsed)) {
                  value = new Date(parsed).toISOString().split('T')[0];
                }
                break;
              // text, email, url, phone remain as strings
            }
          }

          processedRow[targetField] = value;
        }
      });

      return processedRow;
    }).filter(row => {
      // Filter out completely empty rows
      const hasData = Object.keys(row).some(key =>
        key !== '_rowIndex' && row[key] !== null && row[key] !== undefined && row[key] !== ''
      );
      return hasData;
    });
  }, [data, fieldMappings, columnTypes]);

  const validationResults = useMemo(() => {
    const results = {
      valid: [],
      invalid: [],
      duplicates: [],
      warnings: []
    };

    const seenValues = new Set();

    processedData.forEach((row, index) => {
      const issues = [];
      const warnings = [];

      // Check required fields
      targetConfig.requiredFields.forEach(field => {
        if (!row[field] || row[field] === '') {
          issues.push(`Missing required field: ${field}`);
        }
      });

      // Create a duplicate key based on main identifying fields
      let duplicateKey = '';
      if (target === 'master_checklist') {
        duplicateKey = `${row.category || ''}-${row.task || ''}-${row.type || ''}`;
      } else if (target === 'dealer_locations') {
        duplicateKey = `${row.name || ''}-${row.group_id || ''}`;
      } else if (target === 'addresses') {
        duplicateKey = `${row.street_name || ''}-${row.town_city || ''}-${row.postcode || ''}`;
      } else if (target === 'links') {
        duplicateKey = `${row.link_key || ''}-${row.link_type || ''}`;
      } else if (target === 'contact_messages') {
        duplicateKey = `${row.message_name || ''}-${row.message_type || ''}`;
      } else if (target === 'dropdown_options') {
        duplicateKey = `${row.option_type || ''}-${row.option_value || ''}`;
      }

      if (duplicateKey && seenValues.has(duplicateKey)) {
        results.duplicates.push({ ...row, _issues: ['Duplicate entry detected'] });
      } else {
        if (duplicateKey) seenValues.add(duplicateKey);

        if (issues.length > 0) {
          results.invalid.push({ ...row, _issues: issues, _warnings: warnings });
        } else {
          if (warnings.length > 0) {
            results.warnings.push({ ...row, _warnings: warnings });
          }
          results.valid.push(row);
        }
      }
    });

    return results;
  }, [processedData, targetConfig, target]);

  const displayRows = showAllRows ? processedData : processedData.slice(0, 10);

  const renderPreviewTable = () => {
    const mappedFields = Object.values(fieldMappings);

    return (
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Row
              </th>
              {mappedFields.map(field => (
                <th key={field} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {field}
                  {targetConfig.requiredFields.includes(field) && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayRows.map((row, index) => {
              const hasErrors = validationResults.invalid.includes(row);
              const isDuplicate = validationResults.duplicates.includes(row);

              return (
                <tr key={index} className={`
                  ${hasErrors ? 'bg-red-50' : isDuplicate ? 'bg-yellow-50' : ''}
                `}>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    {row._rowIndex}
                  </td>
                  {mappedFields.map(field => (
                    <td key={field} className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      <div className="max-w-xs truncate">
                        {row[field] !== null && row[field] !== undefined
                          ? String(row[field])
                          : <span className="text-gray-400">-</span>
                        }
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {processedData.length > 10 && !showAllRows && (
          <div className="bg-gray-50 px-4 py-2 text-center">
            <button
              onClick={() => setShowAllRows(true)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Show all {processedData.length} rows
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderValidationSummary = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="text-green-600" size={20} />
          <h4 className="font-medium text-green-800">Valid Rows</h4>
        </div>
        <p className="text-2xl font-bold text-green-700">{validationResults.valid.length}</p>
        <p className="text-sm text-green-600">Ready to import</p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="text-red-600" size={20} />
          <h4 className="font-medium text-red-800">Invalid Rows</h4>
        </div>
        <p className="text-2xl font-bold text-red-700">{validationResults.invalid.length}</p>
        <p className="text-sm text-red-600">Will be skipped</p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="text-yellow-600" size={20} />
          <h4 className="font-medium text-yellow-800">Duplicates</h4>
        </div>
        <p className="text-2xl font-bold text-yellow-700">{validationResults.duplicates.length}</p>
        <p className="text-sm text-yellow-600">Need action</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Database className="text-blue-600" size={20} />
          <h4 className="font-medium text-blue-800">Total Rows</h4>
        </div>
        <p className="text-2xl font-bold text-blue-700">{processedData.length}</p>
        <p className="text-sm text-blue-600">In file</p>
      </div>
    </div>
  );

  const handleExportPreview = () => {
    const csv = [
      // Headers
      ['Row', ...Object.values(fieldMappings), 'Status', 'Issues'].join(','),
      // Data
      ...processedData.map(row => {
        let status = 'Valid';
        let issues = '';

        if (validationResults.invalid.includes(row)) {
          status = 'Invalid';
          issues = row._issues?.join('; ') || '';
        } else if (validationResults.duplicates.includes(row)) {
          status = 'Duplicate';
          issues = 'Duplicate entry detected';
        }

        return [
          row._rowIndex,
          ...Object.values(fieldMappings).map(field => `"${row[field] || ''}"`),
          status,
          `"${issues}"`
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `import_preview_${target}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold">Import Preview</h3>
          <p className="text-gray-600">
            Review the data that will be imported into {targetConfig.name}
          </p>
        </div>
        <button
          onClick={handleExportPreview}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Download size={16} />
          Export Preview
        </button>
      </div>

      {renderValidationSummary()}

      {/* Duplicate Handling */}
      {validationResults.duplicates.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-medium text-yellow-800 mb-3">Duplicate Handling</h4>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="duplicateAction"
                value="skip"
                checked={selectedDuplicateAction === 'skip'}
                onChange={(e) => setSelectedDuplicateAction(e.target.value)}
              />
              <span className="text-sm">Skip duplicate rows (recommended)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="duplicateAction"
                value="update"
                checked={selectedDuplicateAction === 'update'}
                onChange={(e) => setSelectedDuplicateAction(e.target.value)}
              />
              <span className="text-sm">Update existing records</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="duplicateAction"
                value="create"
                checked={selectedDuplicateAction === 'create'}
                onChange={(e) => setSelectedDuplicateAction(e.target.value)}
              />
              <span className="text-sm">Create new records anyway</span>
            </label>
          </div>
        </div>
      )}

      {/* Validation Issues */}
      {validationResults.invalid.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-medium text-red-800 mb-3">Validation Issues</h4>
          <div className="text-sm text-red-700 space-y-1">
            {validationResults.invalid.slice(0, 5).map((row, index) => (
              <div key={index}>
                Row {row._rowIndex}: {row._issues?.join(', ')}
              </div>
            ))}
            {validationResults.invalid.length > 5 && (
              <div className="text-red-600">
                ... and {validationResults.invalid.length - 5} more issues
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview Table */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <FileText className="text-gray-600" size={20} />
          <h4 className="text-lg font-medium">Data Preview</h4>
          <span className="text-sm text-gray-500">
            (showing {displayRows.length} of {processedData.length} rows)
          </span>
        </div>
        {renderPreviewTable()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          disabled={isProcessing}
        >
          <ChevronLeft size={16} />
          Back to Field Mapping
        </button>
        <button
          onClick={() => onConfirm({
            duplicateAction: selectedDuplicateAction,
            validRows: validationResults.valid.length,
            invalidRows: validationResults.invalid.length,
            duplicateRows: validationResults.duplicates.length
          })}
          disabled={isProcessing || validationResults.valid.length === 0}
          className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Importing...
            </>
          ) : (
            <>
              Import {validationResults.valid.length} Rows
              <Database size={16} />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ImportPreview;